import * as React from 'react';
import {useSelector} from "react-redux";
import {has} from "lodash";
import * as moment from 'moment';
import {TopicEntity} from "../../contract/topic";
import useCurrentRoute from "../../hooks/useCurrentRoute";
import {INITIAL_ROUTE} from "../../hooks/useRoutes";
import {RootState} from "../../redux/store";
import {RowContainer, SkeletonLoader} from "../Containers";
import {Tag} from "../Content";
import {ArticleContext} from "./ArticleCard";
import {PublicationStatus} from "../../config";
import ArticleTopicContent from "./ArticleTopicContent";

moment.locale('nl');

interface Props {
  size: number;
  compact?: boolean;
  slug?: string;
  label?: string;
  priority?: number;
  className?: string;
  final?: boolean;
  style?: {[key: string]: number|string}
}

const ArticleTopics: React.FC<Props> = ({
  size = 6,
  style,
  compact = false,
  ...props
}): React.ReactElement => {
  const pathname = useCurrentRoute();
  const {articles_visible, isStaging, topics} = useSelector((state: RootState) => {
    const topics = Object.values(state.entities.topics.byId);
    const {config, routes} = state.application;
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles_visible} = route;

    return {
      articles_visible: (articles_visible || []),
      isStaging: config.environment === 'staging',
      topics,
    }
  });
  let visible;

  return (
    <ArticleContext.Consumer>
      {({content: {id: uuid, topics: ids, publication_status, visible: isVisible}}) => {
        visible = isVisible || (articles_visible || []).find(id => id === String(uuid));

        return visible ? (
          <RowContainer
            style={{
              width: '100%',
              alignItems: 'center',
              flexFlow: compact ? 'row nowrap' : 'row wrap',
              marginTop: compact ? '-1px' : 'unset',
              ...style
            }}
          >
            {isStaging && publication_status !== String(PublicationStatus.PUBLISHED) && (
              <Tag
                tabIndex={-1}
                className={"theme--bg-overlay-color theme--primary-color fw-600 f-7"}
                style={{whiteSpace: 'nowrap', margin: compact ? '0 10px 0 0' : '10px 10px 0 0'}}
              >
                <label>{publication_status}</label>
              </Tag>
            )}

            {(ids || []).map((id: string, index: number): React.ReactElement => {
              const topic = (topics || []).find((topic: TopicEntity) => String(topic.id) === id);

              return topic && (
                <ArticleTopicContent
                  slug={topic.slug}
                  label={topic.title}
                  priority={1}
                  key={topic.id}
                  className={`f-${String(size)}`}
                  compact={compact}
                  size={size}
                  final={index === (ids.length - 1)}
                  style={{margin: '10px 10px 0 0'}}
                  {...props}
                />
              )
            })}
          </RowContainer>
        ) : (
          <SkeletonLoader {...props} />
        )
      }}
    </ArticleContext.Consumer>
  )
};

export default ArticleTopics;
